/*
 * A visitor RIDING a ribbon: pure geometry and decisions (no DOM, no d3), so it can be tested. When a funnel visitor steps from one step to the next, their bubble follows the ribbon between
 * the two boxes instead of drifting there by physics (Chris: "I want to WATCH the bubbles move along the funnel paths. It seems so disconnected."). The page does the moving (clusterService.ts
 * `travelBubbleAlong`); this module says whether a move rides, and along which points.
 */
import { CentreSample } from "./funnelRibbons";
import { Progress } from "./funnelProgress";

export interface Pt {
  x: number;
  y: number;
}

/** A steady number in [-1, 1] from an id, so a visitor keeps the same lane in every ribbon and two visitors rarely share one. */
export const laneOf = (id: string): number => {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }

  return ((h >>> 0) % 2001) / 1000 - 1;
};

/** How much of the ribbon's half width the lanes use: a visitor stays inside the band, never on its edge. */
export const LANE_SPREAD = 0.6;

/** The points a visitor follows: the ribbon's centre line shifted sideways by their lane (a share of the half width THERE, so the lane narrows as the ribbon does), then the next box's centre. */
export const ridePoints = (centre: CentreSample[], lane: number, destination: Pt): Pt[] => [
  ...centre.map((c) => ({ x: c.x + c.nx * c.half * LANE_SPREAD * lane, y: c.y + c.ny * c.half * LANE_SPREAD * lane })),
  destination,
];

/** Total length of a polyline. */
export const lengthOf = (points: Pt[]): number => {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);

  return total;
};

/** The point a given FRACTION of the way along a polyline, measured by length (so speed along it is even whatever the spacing of the points). */
export const pointAlong = (points: Pt[], fraction: number): Pt => {
  if (!points.length) return { x: 0, y: 0 };
  const f = Math.min(1, Math.max(0, fraction));
  const total = lengthOf(points);
  if (total === 0 || f === 0) return points[0];
  let want = f * total;
  for (let i = 1; i < points.length; i++) {
    const seg = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    if (want <= seg || i === points.length - 1) {
      const u = seg === 0 ? 1 : Math.min(1, want / seg);

      return { x: points[i - 1].x + (points[i].x - points[i - 1].x) * u, y: points[i - 1].y + (points[i].y - points[i - 1].y) * u };
    }
    want -= seg;
  }

  return points[points.length - 1];
};

/** A slow start and a soft landing, like the app's own bubbles. */
export const ease = (t: number): number => {
  const k = Math.min(1, Math.max(0, t));

  return k * k * (3 - 2 * k);
};

/** How long a ride takes: a steady pace, but never a blink and never a crawl. */
export const rideDurationMs = (lengthPx: number): number => Math.min(2200, Math.max(700, (lengthPx / 260) * 1000));

/**
 * Does this move ride a ribbon? Only a funnel visitor stepping from step k to step k+1 (the page they left IS step k, the page they reached IS step k+1) AND who had reached exactly step k
 * (not further, not before): a visitor who wandered and came back to the next step from somewhere else has no ribbon under that move. `progress` is where they were BEFORE this move.
 */
export const ridesRibbon = (from: string | null | undefined, to: string, pages: string[], progress: Progress | undefined, strict: boolean): boolean => {
  if (!from || !progress) return false;
  const k = pages.indexOf(from);
  if (k < 0 || pages[k + 1] !== to) return false;
  if (progress.k !== k + 1) return false;                     // they must have been AT this step, not past it
  if (strict && progress.broken) return false;

  return true;
};
